import React, { useEffect, useState } from 'react';
import orderSuccessMp3 from '../assets/audio/order-success.mp3';
import API_BASE_URL from '../config/api.config';

const OrderCompleted = ({
  isOpen, orderNumber, selectedLanguage = 'ko', willWriteReview, onClose,
  channel = 'naver', kakaoPlaceId, benefitName, storeId, tableCode, note,
}) => {
  const isKakao = channel === 'kakao';
  const accent = isKakao ? '#FDCD28' : '#03C75A';       // 카카오=노랑 / 네이버=초록
  const accentInk = isKakao ? '#191919' : '#FFFFFF';    // 노란 버튼 위 글자는 진하게

  const chName = {
    ko: isKakao ? '카카오' : '네이버',
    en: isKakao ? 'Kakao' : 'Naver',
    ja: isKakao ? 'カカオ' : 'Naver',
    zh: isKakao ? 'Kakao' : 'Naver',
  }[selectedLanguage] || (isKakao ? '카카오' : '네이버');

  const benefit = benefitName || '';

  const translations = {
    ko: {
      mainText: '주문이 접수됐어요', orderDate: '주문일시', orderNumber: '주문번호',
      reviewTitle: isKakao ? '카카오 리뷰를 작성해주세요' : '직원이 네이버 리뷰 영수증을\n가져다 드릴게요',
      reviewSubtitle: isKakao ? '저희가 확인하고 가져다 드릴게요' : '잠시만 기다려주세요!',
      backToMenu: '확인',
      kakaoGo: '카카오 리뷰 쓰러가기',
      later: '나중에 쓸게요',
      laterHint: 'QR을 다시 찍으면 언제든 쓸 수 있어요',
    },
    en: {
      mainText: 'Order received', orderDate: 'Date', orderNumber: 'Order #',
      reviewTitle: isKakao ? 'Please write a Kakao review' : 'Staff will bring you\nthe Naver review receipt',
      reviewSubtitle: isKakao ? "We'll bring it after we check" : 'Please wait a moment!',
      backToMenu: 'Confirm',
      kakaoGo: 'Write a Kakao review',
      later: 'Write later',
      laterHint: '(Please scan the QR again when you write)',
    },
    ja: {
      mainText: '注文を受け付けました', orderDate: '注文日時', orderNumber: '注文番号',
      reviewTitle: isKakao ? 'カカオレビューを書いてください' : 'スタッフがNaverレビュー\nレシートをお持ちします',
      reviewSubtitle: isKakao ? '確認後にお持ちします' : '少々お待ちください！',
      backToMenu: '確認',
      kakaoGo: 'カカオレビューを書く',
      later: '後で書く',
      laterHint: '(レビュー時にQRを再度スキャンしてください)',
    },
    zh: {
      mainText: '订单已接收', orderDate: '订单时间', orderNumber: '订单号',
      reviewTitle: isKakao ? '请撰写Kakao评价' : '员工将为您送上\nNaver评论小票',
      reviewSubtitle: isKakao ? '确认后为您送上' : '请稍候！',
      backToMenu: '确认',
      kakaoGo: '去写Kakao评价',
      later: '稍后再写',
      laterHint: '(写评价时请重新扫描二维码)',
    },
  };

  const t = translations[selectedLanguage] || translations.ko;

  const [orderTime, setOrderTime] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setOrderTime(prev => prev || new Date());
      try {
        const audio = new Audio(orderSuccessMp3);
        audio.volume = 0.5;
        audio.play().catch(() => {});
      } catch (e) {}
    } else {
      setOrderTime(null);
    }
  }, [isOpen]);

  useEffect(() => {
    const scaleUI = () => {
      const wrapper = document.querySelector('.order-completed-wrapper');
      if (wrapper) {
        const viewportWidth = window.innerWidth;
        const scale = Math.min(viewportWidth / 360, 1.5);
        wrapper.style.zoom = scale;
      }
    };
    if (isOpen) {
      scaleUI();
      window.addEventListener('resize', scaleUI);
    }
    return () => window.removeEventListener('resize', scaleUI);
  }, [isOpen]);

  if (!isOpen) return null;

  const now = orderTime || new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const date = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const dateTimeString = `${year}-${month}-${date} ${hours}:${minutes}`;

  // 카카오 리뷰 쓰러가기: 폴링 세션 생성(best-effort) 후 카카오 리뷰 페이지로 이동
  const handleKakaoReview = async () => {
    const url = `https://place.map.kakao.com/${kakaoPlaceId}#addreview?rate=5`;
    try {
      if (storeId && tableCode && kakaoPlaceId) {
        const sessionId = `${storeId}-${tableCode}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        await fetch(`${API_BASE_URL}/api/review/start-review`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionId, storeId, tableCode, placeId: kakaoPlaceId, channel: 'kakao' }),
        }).catch(() => {});
      }
    } finally {
      window.location.href = url;
    }
  };

  const showReview = willWriteReview;
  const showKakaoButtons = showReview && isKakao && kakaoPlaceId;

  return (
    <div className="order-completed">
      <button className="back-arrow-button" onClick={onClose} aria-label="뒤로 가기">
        <svg width="11" height="20" viewBox="0 0 11 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M10 1L1 10L10 19" stroke="#191919" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      <div className="order-completed-wrapper">
        <div className={`receipt-container ${showReview ? 'with-review' : ''}`}>
          <div className="check-icon-wrapper" style={isKakao ? { boxShadow: '0px 4.4px 11px #FBEBA8' } : undefined}>
            <div className="check-icon-inner" style={isKakao ? { background: '#FDCD28' } : undefined}>
              <svg width="32" height="24" viewBox="0 0 32 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M2.4375 12.1875L11.4375 21.1875L28.6875 2.43752" stroke="white" strokeWidth="4.875" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>

          <div className="receipt-ticket">
            <h2 className="receipt-title">{t.mainText}</h2>
            <div className="dashed-line"></div>
            <div className="order-detail">
              <div className="order-row">
                <span className="row-label">{t.orderDate}</span>
                <span className="row-value">{dateTimeString}</span>
              </div>
              <div className="order-row">
                <span className="row-label">{t.orderNumber}</span>
                <span className="row-value">{orderNumber || 3}</span>
              </div>
            </div>
            <div className="dashed-line"></div>
          </div>

          <div className="receipt-edge"></div>
        </div>

        {showReview && (
          <div className="review-message">
            {isKakao ? (
              <svg className="receipt-icon" width="22" height="22" viewBox="0 0 450 450" xmlns="http://www.w3.org/2000/svg" aria-label="카카오">
                <path fill="#fae000" transform="matrix(1.03079 0 0 1.03079 -163.262 -511.396)" d="M220.265 496.122c-34.176 0-61.88 27.703-61.88 61.866v312.815c0 34.176 27.704 61.879 61.88 61.879h312.814c34.176 0 61.867-27.703 61.867-61.88V557.989c0-34.163-27.69-61.866-61.867-61.866z" />
                <path fill="#007dfc" transform="matrix(1.03079 0 0 1.03079 -163.262 -511.396)" d="M378.298 552.754c-63.196-.038-114.244 50.744-114.333 113.586-.05 28.5 11.666 52.834 29.312 73.596 32.187 37.849 60.359 77.712 75.736 125.556 1.381 4.307 3.459 9.222 9.4 9.07 6.46-.165 8.195-5.662 9.728-10.311 14.428-43.778 38.546-81.59 69.01-115.942 18.102-20.407 32.63-43.93 35.063-71.57 5.94-67.338-47.312-124.91-113.916-123.985m38.863 114.662c-.152 20.8-18.456 38.99-39.04 38.8-20.774-.19-38.8-18.444-38.723-39.167.076-20.71 18.392-38.875 39.09-38.774 20.673.089 38.825 18.469 38.673 39.141" />
              </svg>
            ) : (
              <svg className="receipt-icon" width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4.58301 19.25V4.58333C4.58301 4.0971 4.77616 3.63079 5.11998 3.28697C5.4638 2.94315 5.93011 2.75 6.41634 2.75H15.583C16.0692 2.75 16.5356 2.94315 16.8794 3.28697C17.2232 3.63079 17.4163 4.0971 17.4163 4.58333V19.25L14.6663 17.4167L12.833 19.25L10.9997 17.4167L9.16634 19.25L7.33301 17.4167L4.58301 19.25Z" stroke={accent} strokeWidth="1.83333" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M8.25 6.41669H13.75" stroke={accent} strokeWidth="1.83333" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M8.25 10.0833H13.75" stroke={accent} strokeWidth="1.83333" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M8.25 13.75H11" stroke={accent} strokeWidth="1.83333" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
            <p className="review-title">{t.reviewTitle}</p>
            <p className="review-subtitle">{t.reviewSubtitle}</p>
          </div>
        )}

        {showKakaoButtons ? (
          <div className="kakao-cta">
            <button
              className="kakao-go-button"
              style={{ background: accent, color: accentInk, borderColor: accent }}
              onClick={handleKakaoReview}
            >
              {t.kakaoGo}
            </button>
            <button className="later-button" onClick={() => { setTimeout(onClose, 140); }}>
              <span className="later-main">{t.later}</span>
              <span className="later-hint">{t.laterHint}</span>
            </button>
          </div>
        ) : (
          <button
            className="back-to-menu-button"
            style={{ borderColor: accent, color: accent }}
            onClick={() => { setTimeout(onClose, 140); }}
          >
            {t.backToMenu}
          </button>
        )}
      </div>

      <style jsx="true">{`
        .order-completed { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: #F7F8FA; z-index: 1002; display: flex; flex-direction: column; overflow: hidden; }
        .order-completed-wrapper { width: 360px; height: 100%; margin: 0 auto; position: relative; display: flex; flex-direction: column; align-items: center; transform-origin: top center; }
        @media screen and (min-width: 361px) { .order-completed-wrapper { zoom: calc(100vw / 360); } }
        @media screen and (min-width: 540px) { .order-completed-wrapper { zoom: 1.5; } }
        @media screen and (max-width: 360px) { .order-completed-wrapper { width: 100vw; zoom: calc(100vw / 360); } }
        .back-arrow-button { position: absolute; top: 14px; left: 14px; width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; background: transparent; border: none; cursor: pointer; z-index: 10; padding: 0; }
        .back-arrow-button:active { opacity: 0.5; }
        .receipt-container { position: relative; margin-top: 200px; display: flex; flex-direction: column; align-items: center; animation: scaleIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1); }
        .receipt-container.with-review { margin-top: 150px; }
        @keyframes scaleIn { 0% { transform: scale(0.8); opacity: 0; } 100% { transform: scale(1); opacity: 1; } }
        .check-icon-wrapper { position: absolute; top: -38px; left: 50%; transform: translateX(-50%); width: 74.8px; height: 74.8px; background: #F7F8FA; box-shadow: 0px 4.4px 11px #D1F4E1; border-radius: 50%; display: flex; align-items: center; justify-content: center; z-index: 2; }
        .check-icon-inner { width: 66px; height: 66px; background: #03C75A; border-radius: 50%; display: flex; align-items: center; justify-content: center; }
        .receipt-ticket { width: 222.2px; background: #FFFFFF; border-radius: 15.4px 15.4px 0 0; padding: 52.8px 24.2px 17.6px; display: flex; flex-direction: column; align-items: center; gap: 17.6px; box-sizing: border-box; }
        .receipt-title { margin: 0; font-family: 'Pretendard', sans-serif; font-weight: 700; font-size: 17.6px; line-height: 21px; text-align: center; letter-spacing: -0.03em; color: #191919; }
        .dashed-line { width: 173.8px; height: 0; border-top: 1.54px dashed #D6D6D6; }
        .order-detail { display: flex; flex-direction: column; width: 173.8px; gap: 11px; }
        .order-row { display: flex; flex-direction: row; justify-content: space-between; align-items: center; width: 100%; }
        .row-label { font-family: 'Pretendard', sans-serif; font-weight: 500; font-size: 12.65px; line-height: 15px; color: #ABABAB; }
        .row-value { font-family: 'Pretendard', sans-serif; font-weight: 700; font-size: 12.65px; line-height: 15px; color: #191919; }
        .receipt-edge { width: 222.2px; height: 13.2px; background: radial-gradient(circle at 6.6px 13.2px, #F7F8FA 6.6px, #FFFFFF 6.7px); background-size: 13.2px 13.2px; background-repeat: repeat-x; }
        .review-message { margin-top: 22px; display: flex; flex-direction: column; align-items: center; gap: 7.7px; opacity: 0; animation: fadeIn 0.6s ease-out 0.3s forwards; padding: 0 24px; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        .receipt-icon { width: 22px; height: 22px; }
        .review-title { margin: 0; font-family: 'Pretendard', sans-serif; font-weight: 600; font-size: 13.75px; line-height: 150%; text-align: center; white-space: pre-line; background: linear-gradient(100deg, #767676 0%, #767676 22%, #c4c4c4 50%, #767676 78%, #767676 100%); background-size: 360% 100%; -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent; color: transparent; animation: review-shimmer 2.6s ease-in-out infinite; }
        @keyframes review-shimmer { 0% { background-position: 130% 0; } 100% { background-position: -30% 0; } }
        .review-subtitle { margin: 0; font-family: 'Pretendard', sans-serif; font-weight: 500; font-size: 12.1px; line-height: 14px; text-align: center; color: #ABABAB; }
        .kakao-cta { position: fixed; bottom: 16px; left: 50%; margin-left: -165px; width: 330px; display: flex; flex-direction: column; gap: 8px; }
        .kakao-go-button { width: 100%; height: 56px; border: 1.5px solid; border-radius: 16px; font-size: 17px; font-weight: bold; cursor: pointer; box-shadow: 0 1px 4px rgba(0,0,0,0.06); transition: transform 0.12s ease; }
        .kakao-go-button:active { transform: scale(0.96); }
        .later-button { width: 100%; min-height: 48px; padding: 6px; border: none; border-radius: 14px; background: transparent; cursor: pointer; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 2px; }
        .later-button:active { opacity: 0.6; }
        .later-main { font-size: 15px; font-weight: 600; color: #767676; }
        .later-hint { font-size: 11.5px; color: #ABABAB; }
        .back-to-menu-button { position: fixed; bottom: 16px; left: 50%; margin-left: -165px; width: 330px; height: 56px; border: 1.5px solid; border-radius: 16px; background: #FFFFFF; font-size: 17px; font-weight: bold; cursor: pointer; box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06); transition: transform 0.12s cubic-bezier(0.25, 0.46, 0.45, 0.94), box-shadow 0.12s ease; transform-origin: center; }
        .back-to-menu-button:active { transform: scale(0.92); }
      `}</style>
    </div>
  );
};

export default OrderCompleted;
